import { useEffect, useState } from "react";
import { Header, Spreadsheet } from "./components";
import { CellData, SpreadsheetData } from "./types";
import { motion } from "framer-motion";
import Toast from "./components/Toast";
import GlyphBackdrop from "./GlyphBackdrop";

// Add this near the top of the file, after the imports
const writingAnimation = `
@keyframes writing {
  0% {
    stroke-dashoffset: 1000;
  }
  100% {
    stroke-dashoffset: 0;
  }
}

.animate-writing {
  animation: writing 1.5s linear infinite;
}
`;

// Add this right after the imports
const style = document.createElement("style");
style.textContent = writingAnimation;
document.head.appendChild(style);


export type ToastDetail = {
  message?: string;
  type?: "success" | "error" | "info";
  isShowing?: boolean;
};

const STORAGE_KEY = "tavily-sheets:table";

const emptyTable = (): SpreadsheetData => ({
  headers: Array(5).fill(""),
  rows: Array(5)
    .fill(0)
    .map(() => Array(5).fill({ value: "" })),
});

// Restores the table saved before a refresh. Cells that were still researching
// come back as failed so the next run retries them.
const loadSavedTable = (): SpreadsheetData => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "null");
    if (
      !saved ||
      !Array.isArray(saved.headers) ||
      !Array.isArray(saved.rows) ||
      !saved.rows.every((row: unknown) => Array.isArray(row))
    ) {
      return emptyTable();
    }
    return {
      headers: saved.headers.map((header: unknown) => String(header ?? "")),
      rows: saved.rows.map((row: CellData[]) =>
        row.map((cell) =>
          cell?.loading
            ? { value: "", status: "failed", error: "Interrupted before this cell finished" }
            : { ...cell, value: String(cell?.value ?? "") },
        ),
      ),
    };
  } catch {
    return emptyTable();
  }
};

function App() {
  const [toastDetail, setToastDetail] = useState<ToastDetail>({});
  const [data, setData] = useState<SpreadsheetData>(loadSavedTable);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.warn("Could not save the table", error);
    }
  }, [data]);

  return (
    <div className="min-h-screen-dvh w-full relative">
      <div className="sky-background" aria-hidden="true">
        <img src="/tavily-sky.webp" alt="" />
        <GlyphBackdrop />
      </div>

      {toastDetail.isShowing && (
        <Toast
          message={toastDetail.message}
          type={toastDetail.type}
          onClose={() => setToastDetail({})}
        />
      )}

      <div className="relative z-10 mx-auto w-[calc(100%-2rem)] sm:w-[calc(100%-4rem)] max-w-[1680px] pb-16">
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Header />
        </motion.div>

        <div className="relative mt-8">
          {/* Spreadsheet Component */}
          <motion.div
            className="relative"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Spreadsheet
              data={data}
              setData={setData}
              setToast={setToastDetail}
            />
          </motion.div>
        </div>
      </div>

      <a
        className="ot-sdk-show-settings text-[11px] underline"
        href="#"
        style={{
          position: "fixed",
          bottom: "1rem",
          right: "1rem",
          zIndex: 50,
          color: "var(--color-muted)",
        }}
      >
        Cookie Settings
      </a>
    </div>
  );
}

export default App;
